<template>
    <div class="Tabs">
        <div class="Tabs-Header" role="tablist">
            <button
                v-for="tab in resolvedTabs"
                :key="tab.key"
                class="Tabs-Tab"
                :class="{ 'is-active': tab.key === internalActive }"
                role="tab"
                :aria-selected="tab.key === internalActive"
                :aria-controls="`panel-${tab.key}`"
                @click="selectTab(tab.key)"
            >
                <i v-if="tab.icon" :class="`ri-${tab.icon}-line Tabs-Icon`"></i>
                <span class="Tabs-Label">{{ tab.label }}</span>
                <span
                    v-if="tab.badge !== undefined"
                    class="Tabs-Badge"
                    :class="tab.badgeClass || 'is-basic'"
                >
                    {{ tab.badge }}
                </span>
            </button>
        </div>
        <div class="Tabs-Content">
            <slot :active="internalActive"></slot>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Tabs',
    props: {
        value: { type: String, default: null },
        tabs: { type: Array, default: () => [] },
        hideEmpty: { type: Boolean, default: false },
    },
    data() {
        return {
            internalActive: this.value || (this.tabs[0] && this.tabs[0].key) || null,
        };
    },
    computed: {
        resolvedTabs() {
            if (!this.hideEmpty) {
                return this.tabs;
            }
            return this.tabs.filter((t) => t.badge === undefined || Number(t.badge) > 0);
        },
    },
    watch: {
        value(val) {
            this.internalActive = val;
        },
        tabs: {
            handler(next) {
                if (!next || !next.length) return;
                const keys = next.map((t) => t.key);
                if (!keys.includes(this.internalActive)) {
                    this.internalActive = next[0].key;
                    this.$emit('input', this.internalActive);
                }
            },
            deep: true,
        },
    },
    methods: {
        selectTab(key) {
            if (key === this.internalActive) return;
            this.internalActive = key;
            this.$emit('input', key);
            this.$emit('change', key);
        },
    },
};
</script>

<style lang="scss">
.Tabs {
    display: flex;
    flex-direction: column;

    @media (min-width: $breakpoint-tablet-landscape) {
        gap: 0;

        & .Card {
            border-top-left-radius: 0;
        }
    }
}

.Tabs-Header {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: -1px;
}

.Tabs-Tab {
    appearance: none;
    border: 0.5px solid var(--color-border-light);
    border-radius: 5px;
    color: var(--color-text-inactive);
    padding: 8px 14px;
    font-size: 0.9rem;
    line-height: 1;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;

    @media (min-width: $breakpoint-tablet-landscape) {
        border-radius: 5px 5px 0 0;
    }

    &:hover {
        border-color: var(--color-blue);
        background: rgba(var(--color-blue-rgb), 0.08);
        color: var(--color-blue);
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        border-color: var(--color-blue);
        box-shadow: 0 0 0 2px rgba(var(--color-blue-rgb), 0.2);
        z-index: 3;
    }

    &.is-active {
        border-color: var(--color-border-light);
        background: var(--color-white);
        color: var(--color-text);
        cursor: default;

        @media (min-width: $breakpoint-tablet-landscape) {
            border-bottom: 0.5px solid var(--color-white);
            z-index: 2;
        }
    }
}

.Tabs-Icon {
    font-size: 1rem;
    opacity: 0.8;
    color: currentColor;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.Tabs-Badge {
    border-radius: 10px;
    padding: 3px 8px 2px 7px;
    font-size: 0.75rem;

    &.is-basic {
        background-color: var(--color-white);
        color: var(--color-text);
        border: solid 0.5px var(--color-text-inactive);
    }

    &.is-primary {
        background-color: var(--color-grey);
        color: var(--color-text);
    }

    &.is-warning {
        background-color: var(--color-yellow-end);
        color: black;
    }

    &.is-success {
        background-color: var(--color-green-dark);
        color: white;
    }

    &.is-error,
    &.is-red {
        background-color: var(--color-red);
        color: white;
    }
}
</style>
